import { feetInchesToInches, isValidFeetInchesFormat } from './utils.js';

// Active Vessel Type State
let activeVesselType = 'monohull';
let currentForm = null;
let currentContainer = null;

// Dynamic SVG DOM references (re-bound on every SVG load)
let sideviewHull, sideviewKeel, sideviewMast, sailsGroup, boatSideviewGroup;
let frontviewHull, frontviewKeel, frontviewMast;

let lineLength, leftTriLength, rightTriLength, textLength, measureLength;
let lineBeam, leftTriBeam, rightTriBeam, textBeam, measureBeam;
let lineMast, upperTriMast, textMast, measureMast;
let lineDraft, lowerTriDraft, textDraft, measureDraft;

// Core SVG Layout Constants
const WATERLINE_Y = 719.23;
const DECK_Y = 677.84;
const PROFILE_CENTER_X = 493.91; // Center of sideview hull
const FRONT_CENTER_X = 952.87;    // Center of frontview hull

// Format draft as feet and inches (e.g. 6.5 -> 6'6")
export function formatDraft(draftValue) {
  const feet = Math.floor(draftValue);
  const inches = Math.round((draftValue - feet) * 12);
  return `${feet}’${inches}”`;
}

export function getFeetValue(inputValue, defaultValue) {
  if (!inputValue || !inputValue.trim() || !isValidFeetInchesFormat(inputValue.trim())) return defaultValue;
  const inches = feetInchesToInches(inputValue);
  if (isNaN(inches) || inches <= 0) return defaultValue;
  return inches / 12;
}

// Fetch and load SVGs dynamically
async function loadVesselSVG() {
  if (!currentContainer || !currentForm) return;

  const typeValue = (currentForm.elements.type.value || 'Monohull').toLowerCase();
  activeVesselType = typeValue === 'catamaran' ? 'catamaran' : 'monohull';

  const sideFilename = activeVesselType === 'monohull' ? '/monohull_side.svg' : '/catamaran_side.svg';
  const frontFilename = activeVesselType === 'monohull' ? '/monohull_front.svg' : '/catamaran_front.svg';

  const sideContainer = currentContainer.querySelector('#sideview_container') || currentContainer;
  const frontContainer = currentContainer.querySelector('#frontview_container') || currentContainer;

  try {
    const [sideResponse, frontResponse] = await Promise.all([
      fetch(sideFilename),
      fetch(frontFilename)
    ]);

    if (!sideResponse.ok) throw new Error(`HTTP error! status: ${sideResponse.status}`);
    if (!frontResponse.ok) throw new Error(`HTTP error! status: ${frontResponse.status}`);

    const [sideText, frontText] = await Promise.all([
      sideResponse.text(),
      frontResponse.text()
    ]);

    if (sideContainer !== currentContainer) {
      sideContainer.innerHTML = sideText;
    }
    if (frontContainer !== currentContainer) {
      frontContainer.innerHTML = frontText;
    }

    if (sideContainer === currentContainer && frontContainer === currentContainer) {
      currentContainer.innerHTML = sideText + frontText;
    }

    // Bind new SVG elements to variables and apply initial styling
    bindSVGElements();
    initializeSVGStyles();
    updateBoatSVG();
  } catch (error) {
    console.error('Error loading boat SVGs:', error);
  }
}

// Bind SVG elements dynamically
function bindSVGElements() {
  if (!currentContainer) return;

  const sideContainer = currentContainer.querySelector('#sideview_container') || currentContainer.querySelector('#read_sideview_container') || currentContainer;
  const frontContainer = currentContainer.querySelector('#frontview_container') || currentContainer.querySelector('#read_frontview_container') || currentContainer;

  sideviewHull = sideContainer.querySelector('#sideview_monohull');
  sideviewKeel = sideContainer.querySelector('#sideview_keel');
  sideviewMast = sideContainer.querySelector('#sideview_mast');
  sailsGroup = sideContainer.querySelector('#sails');
  boatSideviewGroup = sideContainer.querySelector('#boat_sideview');

  frontviewHull = frontContainer.querySelector('#frontview_monohull');
  frontviewKeel = frontContainer.querySelector('#frontview_keel');
  frontviewMast = frontContainer.querySelector('#frontview_mast');

  lineLength = sideContainer.querySelector('#line_length');
  leftTriLength = sideContainer.querySelector('#left_triagle_length');
  rightTriLength = sideContainer.querySelector('#right_triagle_length');
  textLength = sideContainer.querySelector('#text_length');
  measureLength = sideContainer.querySelector('#measure_length');

  lineBeam = frontContainer.querySelector('#line_beam');
  leftTriBeam = frontContainer.querySelector('#left_triagle_beam');
  rightTriBeam = frontContainer.querySelector('#right_triagle_beam');
  textBeam = frontContainer.querySelector('#text_beam');
  measureBeam = frontContainer.querySelector('#measure_beam');

  lineMast = sideContainer.querySelector('#line_mast');
  upperTriMast = sideContainer.querySelector('#upper_triagle_mast');
  textMast = sideContainer.querySelector('#text_mast');
  measureMast = sideContainer.querySelector('#measure_mast');

  lineDraft = sideContainer.querySelector('#line_draft');
  lowerTriDraft = sideContainer.querySelector('#lower_triagle_draft');
  textDraft = sideContainer.querySelector('#text_draft');
  measureDraft = sideContainer.querySelector('#measure_draft');
}

// Set text centering for labels on startup
function initializeSVGStyles() {
  if (textLength) {
    textLength.setAttribute('text-anchor', 'middle');
    textLength.querySelectorAll('tspan').forEach(tspan => tspan.setAttribute('x', '0'));
  }
  if (textBeam) {
    textBeam.setAttribute('text-anchor', 'middle');
    textBeam.querySelectorAll('tspan').forEach(tspan => tspan.setAttribute('x', '0'));
  }
  if (textMast) {
    textMast.setAttribute('text-anchor', 'end');
    textMast.querySelectorAll('tspan').forEach(tspan => tspan.setAttribute('x', '0'));
  }
  if (textDraft) {
    textDraft.setAttribute('text-anchor', 'end');
    textDraft.querySelectorAll('tspan').forEach(tspan => tspan.setAttribute('x', '0'));
  }

  // Remove absolute matrix translation from text elements to make relative translations easier
  if (textLength) textLength.removeAttribute('transform');
  if (textBeam) textBeam.removeAttribute('transform');
  if (textMast) textMast.removeAttribute('transform');
  if (textDraft) textDraft.removeAttribute('transform');

  // Adjust vertical offset for doubled font-size (28.8px -> 52px)
  if (measureLength) measureLength.setAttribute('y', '52');
  if (measureBeam) measureBeam.setAttribute('y', '52');
  if (measureMast) measureMast.setAttribute('y', '52');
  if (measureDraft) measureDraft.setAttribute('y', '52');
}

// Update all boat SVG parameters
export function updateBoatSVG() {
  if (!sideviewHull || !currentForm) return;

  // Read current control values
  const LOA = getFeetValue(currentForm.elements.boatlength.value, 40);
  const Beam = getFeetValue(currentForm.elements.beam.value, 15);
  const Draft = getFeetValue(currentForm.elements.draft.value, 6.5);
  const Mast = getFeetValue(currentForm.elements.mast.value, 50);

  // Update SVG text measurements
  if (measureLength) measureLength.textContent = `${Math.round(LOA)}’`;
  if (measureBeam) measureBeam.textContent = `${Math.round(Beam)}’`;
  if (measureDraft) measureDraft.textContent = formatDraft(Draft);
  if (measureMast) measureMast.textContent = `${Math.round(Mast)}’`;

  // Update HTML slider value badges
  const lenBadge = document.getElementById('boat-length-val');
  if (lenBadge) lenBadge.textContent = formatDraft(LOA);
  const beamBadge = document.getElementById('boat-beam-val');
  if (beamBadge) beamBadge.textContent = formatDraft(Beam);
  const draftBadge = document.getElementById('boat-draft-val');
  if (draftBadge) draftBadge.textContent = formatDraft(Draft);
  const mastBadge = document.getElementById('boat-mast-val');
  if (mastBadge) mastBadge.textContent = formatDraft(Mast);

  // Scale Factors
  const s_L = LOA / 40;       // Length scale (ref: 40ft)
  const s_B = Beam / 15;      // Beam scale (ref: 15ft)

  // Shift boat to the left if the bow (at PROFILE_CENTER_X + 200 * s_L) goes past x = 720
  const shiftX = Math.max(0, (PROFILE_CENTER_X + 200 * s_L) - 720);

  // ----------------------------------------------------
  // PROFILE VIEW (SIDEVIEW) CALCULATIONS & TRANSFORMS
  // ----------------------------------------------------

  let Y_hull_bottom, Y_keel_bottom;

  if (activeVesselType === 'monohull') {
    // Monohull: Hull height is static, draft slider alters only the keel.
    Y_hull_bottom = 747.15; // Fixed
    Y_keel_bottom = WATERLINE_Y + Draft * 10; // Dynamic keel depth

    // Hull vertical scale = 1.0 (unchanged in height)
    sideviewHull.setAttribute('transform', `translate(${-shiftX}, 0) translate(${PROFILE_CENTER_X}, ${DECK_Y}) scale(${s_L}, 1) translate(${-PROFILE_CENTER_X}, ${-DECK_Y})`);

    // Keel vertical scale (scales between fixed hull bottom Y = 747.15 and dynamic keel bottom)
    if (sideviewKeel) {
      const s_keel_y = (Y_keel_bottom - 747.15) / 37.7;
      sideviewKeel.setAttribute('transform', `translate(${-shiftX}, 0) translate(${PROFILE_CENTER_X}, 747.15) scale(${s_L}, ${s_keel_y}) translate(${-PROFILE_CENTER_X}, -747.15)`);
      sideviewKeel.style.display = '';
    }
  } else {
    // Catamaran: No keel. Draft slider alters hull depth directly.
    Y_hull_bottom = WATERLINE_Y + Draft * 10; // Dynamic hull bottom depth
    Y_keel_bottom = Y_hull_bottom; // Deepest point is the hull bottom

    // Hull vertical scale stretches from deck to dynamic hull bottom
    const s_D_hull = (Y_hull_bottom - DECK_Y) / 69.31;
    sideviewHull.setAttribute('transform', `translate(${-shiftX}, 0) translate(${PROFILE_CENTER_X}, ${DECK_Y}) scale(${s_L}, ${s_D_hull}) translate(${-PROFILE_CENTER_X}, ${-DECK_Y})`);

    // Hide sideview keel
    if (sideviewKeel) {
      sideviewKeel.style.display = 'none';
    }
  }

  // Mast top height above waterline
  const Y_mast_top = WATERLINE_Y - Mast * 10;

  // 1. Sideview Mast Parameters (Anchors bottom to Y_mast_bottom, top to Y_mast_top)
  const mastWidth = 7 * s_L;
  const mastX = PROFILE_CENTER_X + (498.88 - PROFILE_CENTER_X) * s_L - shiftX;
  sideviewMast.setAttribute('x', mastX);
  sideviewMast.setAttribute('width', mastWidth);
  sideviewMast.setAttribute('y', Y_mast_top);

  let Y_mast_bottom;
  if (activeVesselType === 'monohull') {
    Y_mast_bottom = Y_hull_bottom;
  } else {
    const s_D_hull = (Y_hull_bottom - DECK_Y) / 69.31;
    Y_mast_bottom = DECK_Y + 20.0 * s_D_hull;
  }
  sideviewMast.setAttribute('height', Y_mast_bottom - Y_mast_top);

  // 2. Sails (Jib & Mainsail) Transform
  const s_sails_y = (647.62 - Y_mast_top) / 427.92;
  sailsGroup.setAttribute('transform', `translate(${-shiftX}, 0) translate(${PROFILE_CENTER_X}, 647.62) scale(${s_L}, ${s_sails_y}) translate(${-PROFILE_CENTER_X}, -647.62)`);

  // ----------------------------------------------------
  // CROSS-SECTION VIEW (FRONTVIEW) CALCS & TRANSFORMS
  // ----------------------------------------------------

  let Y_front_hull_bottom;

  if (activeVesselType === 'monohull') {
    Y_front_hull_bottom = 748.56; // Fixed hull bottom

    // Hull vertical scale = 1.0 (unchanged)
    frontviewHull.setAttribute('transform', `translate(${FRONT_CENTER_X}, 678.54) scale(${s_B}, 1) translate(${-FRONT_CENTER_X}, -678.54)`);

    // Keel starts at fixed Y = 748.56 and goes down to Y_keel_bottom
    if (frontviewKeel) {
      frontviewKeel.setAttribute('d', `M ${FRONT_CENTER_X - 4},748.56 h8 l-4,${Y_keel_bottom - 748.56} Z`);
      frontviewKeel.style.display = '';
    }
  } else {
    // Catamaran: Draft alters hulls vertically
    Y_front_hull_bottom = WATERLINE_Y + Draft * 10; // Dynamic hull bottom

    // Hull vertical scale stretches from deck to dynamic Y_front_hull_bottom
    const s_D_front_hull = (Y_front_hull_bottom - 678.54) / 70.02;
    frontviewHull.setAttribute('transform', `translate(${FRONT_CENTER_X}, 678.54) scale(${s_B}, ${s_D_front_hull}) translate(${-FRONT_CENTER_X}, -678.54)`);

    // Hide frontview keel
    if (frontviewKeel) {
      frontviewKeel.style.display = 'none';
    }
  }

  // Frontview Mast (anchors bottom to Y_front_mast_bottom, top to Y_mast_top)
  frontviewMast.setAttribute('y', Y_mast_top);

  let Y_front_mast_bottom;
  if (activeVesselType === 'monohull') {
    Y_front_mast_bottom = Y_front_hull_bottom;
  } else {
    const s_D_front_hull = (Y_front_hull_bottom - 678.54) / 70.02;
    Y_front_mast_bottom = 678.54 + 20.0 * s_D_front_hull;
  }
  frontviewMast.setAttribute('height', Y_front_mast_bottom - Y_mast_top);

  // ----------------------------------------------------
  // DIMENSION LINES & LABELS UPDATES
  // ----------------------------------------------------

  // 1. Length Dimension (LOA)
  if (lineLength) {
    const lengthWidth = LOA * 10;
    const lengthStartX = PROFILE_CENTER_X - (lengthWidth / 2) - shiftX;
    lineLength.setAttribute('x', lengthStartX);
    lineLength.setAttribute('width', lengthWidth);

    const deltaLeftLength = lengthStartX - 293.91;
    const deltaRightLength = (PROFILE_CENTER_X + lengthWidth / 2 - shiftX) - 693.91;
    if (leftTriLength) leftTriLength.setAttribute('transform', `translate(${deltaLeftLength}, 0)`);
    if (rightTriLength) rightTriLength.setAttribute('transform', `translate(${deltaRightLength}, 0)`);
    if (textLength) textLength.setAttribute('transform', `translate(${PROFILE_CENTER_X - shiftX}, 880)`);
  }

  // 2. Beam Dimension
  if (lineBeam) {
    const beamWidth = Beam * 10;
    const beamStartX = FRONT_CENTER_X - (beamWidth / 2);
    lineBeam.setAttribute('x', beamStartX);
    lineBeam.setAttribute('width', beamWidth);

    const deltaLeftBeam = beamStartX - 877.87;
    const deltaRightBeam = (FRONT_CENTER_X + beamWidth / 2) - 1027.87;
    if (leftTriBeam) leftTriBeam.setAttribute('transform', `translate(${deltaLeftBeam}, 0)`);
    if (rightTriBeam) rightTriBeam.setAttribute('transform', `translate(${deltaRightBeam}, 0)`);
    if (textBeam) textBeam.setAttribute('transform', `translate(${FRONT_CENTER_X}, 880)`);
  }

  // 3. Mast Dimension
  if (lineMast) {
    const mastLineHeight = Mast * 10;
    const mastLineY = WATERLINE_Y - mastLineHeight;
    lineMast.setAttribute('y', mastLineY);
    lineMast.setAttribute('height', mastLineHeight);

    const deltaMastTop = mastLineY - 219.7;
    if (upperTriMast) upperTriMast.setAttribute('transform', `translate(0, ${deltaMastTop})`);
    if (textMast) textMast.setAttribute('transform', `translate(760, ${WATERLINE_Y - (mastLineHeight / 2) + 8})`);
  }

  // 4. Draft Dimension
  if (lineDraft) {
    const draftLineHeight = Draft * 10;
    lineDraft.setAttribute('y', WATERLINE_Y);
    lineDraft.setAttribute('height', draftLineHeight);

    const deltaDraftBottom = (WATERLINE_Y + draftLineHeight) - 784.85;
    if (lowerTriDraft) lowerTriDraft.setAttribute('transform', `translate(0, ${deltaDraftBottom})`);
    if (textDraft) textDraft.setAttribute('transform', `translate(760, ${WATERLINE_Y + (draftLineHeight / 2) + 8})`);
  }

  // Apply floating sway animation to boat group by default
  if (boatSideviewGroup) {
    boatSideviewGroup.classList.add('swaying-boat');
  }
}

export function initDiagram(formElement, containerElement) {
  currentForm = formElement;
  currentContainer = containerElement;

  const shapeInputs = ['boatlength', 'beam', 'draft', 'mast'];

  // Attach input event listeners for real-time blueprint updates
  shapeInputs.forEach(inputName => {
    const input = formElement.elements[inputName];
    if (input) {
      input.addEventListener('input', updateBoatSVG);
    }
  });

  // Attach type change listener
  const typeSelect = formElement.elements.type;
  if (typeSelect) {
    typeSelect.addEventListener('change', loadVesselSVG);
  }

  // Load the initial SVG
  loadVesselSVG();
}

export function refreshDiagram() {
  loadVesselSVG();
}

export async function renderReadOnlyDiagram(boat, containerElement) {
  currentForm = {
    elements: {
      type: { value: boat.type || 'Monohull' },
      boatlength: { value: String((boat.length || 480) / 12) },
      beam: { value: String((boat.beam || 180) / 12) },
      draft: { value: String((boat.draft || 78) / 12) },
      mast: { value: String((boat.mast || 600) / 12) }
    }
  };
  currentContainer = containerElement;
  activeVesselType = (boat.type || 'Monohull').toLowerCase() === 'catamaran' ? 'catamaran' : 'monohull';
  const sideFilename = activeVesselType === 'monohull' ? '/monohull_side.svg' : '/catamaran_side.svg';
  const frontFilename = activeVesselType === 'monohull' ? '/monohull_front.svg' : '/catamaran_front.svg';
  const sideContainer = currentContainer.querySelector('#read_sideview_container') || currentContainer;
  const frontContainer = currentContainer.querySelector('#read_frontview_container') || currentContainer;
  try {
    const [sideResponse, frontResponse] = await Promise.all([
      fetch(sideFilename),
      fetch(frontFilename)
    ]);
    if (!sideResponse.ok) throw new Error(`HTTP error! status: ${sideResponse.status}`);
    if (!frontResponse.ok) throw new Error(`HTTP error! status: ${frontResponse.status}`);
    const [sideText, frontText] = await Promise.all([
      sideResponse.text(),
      frontResponse.text()
    ]);
    if (sideContainer) sideContainer.innerHTML = sideText;
    if (frontContainer) frontContainer.innerHTML = frontText;
    bindSVGElements();
    initializeSVGStyles();
    updateBoatSVG();
  } catch (error) {
    console.error('Error loading boat read-only SVGs:', error);
  }
}
